import {FC, useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useLocation} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {getUserByToken} from '../core/_requests'
import {wrapCredential} from '../core/credentialCipher'
import {TwoStepChallenge} from '../../profile/core/_models'
import {TwoStepVerification} from './TwoStepVerification'
import {useAuth} from '../core/Auth'
import {
  API_URL,
  GetBrowserData,
  AppClearCache,
  isMegaPay,
  isMobile,
  isAPP,
  brandName,
  isSharBankSSO,
} from '../../../../_metronic/helpers'
import {hasMegaPayBusinessCapabilities} from '../../../../_metronic/helpers/brand'
import {AuthModel} from '../core/_models'
import {FORGOT_PASSWORD_QUERY, rememberLoginRoute} from '../core/credentialNotice'
import {ASA_REGISTRATION_URL, REGISTER_MOBILE_PATH} from '../core/membership'
import './Login.css'
import {CaptchaStore, LoginCaptcha, createCaptchaStore} from './LoginCaptcha'
import {TaxFolderSsoButton} from './TaxFolderSsoButton'

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'نام کاربری حداقل ۳ کاراکتر است.')
    .max(50, 'نام کاربری حداکثر ۵۰ کاراکتر است.')
    .required('نام کاربری را وارد کنید.'),
  password: Yup.string()
    .min(5, 'رمز عبور حداقل ۵ کاراکتر است.')
    // «امنیت و ورود» accepts passwords up to 64 characters.
    .max(64, 'رمز عبور حداکثر ۶۴ کاراکتر است.')
    .required('رمز عبور را وارد کنید.'),
})

const initialValues = {username: '', password: ''}

const Login: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [ssoError, setSsoError] = useState<string>()
  const [captcha, setCaptcha] = useState<CaptchaStore>(() => createCaptchaStore())
  // Set when the password was right and «ورود دومرحله‌ای» asks for the texted code.
  const [challenge, setChallenge] = useState<TwoStepChallenge>()
  const {saveAuth, setCurrentUser} = useAuth()
  const {search} = useLocation()

  const refreshCaptcha = () => setCaptcha(createCaptchaStore())

  const completeSignIn = async (auth: AuthModel) => {
    saveAuth(auth)
    const {data: user} = await getUserByToken(auth.tk || '')
    rememberLoginRoute(false)
    setCurrentUser(user)
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(search)
    const token = searchParams.get('setToken')
    const sso = searchParams.get('sso')
    const ssoNotFound = searchParams.get('ssonotfound')

    if (ssoNotFound) setSsoError(ssoNotFound)

    if (token) {
      axios
        .post(`${API_URL}/auth/verifyToken`, {tk: token})
        .then(async (response) => {
          const auth = response.data as AuthModel
          saveAuth(auth)
          const {data: user} = await getUserByToken(auth.tk || '')
          setCurrentUser(user)
          window.setTimeout(() => window.location.replace('/'), 100)
        })
        .catch(() => setSsoError('ورود یکپارچه انجام نشد. دوباره تلاش کنید.'))
    } else if (sso) {
      getUserByToken('')
        .then((response) => {
          const auth = response.data as AuthModel
          saveAuth(auth)
          setCurrentUser(auth)
          window.setTimeout(() => window.location.replace('/'), 100)
        })
        .catch(() => setSsoError('ورود یکپارچه انجام نشد. دوباره تلاش کنید.'))
    }
  }, [saveAuth, search, setCurrentUser])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setStatus(undefined)

      if (captcha.captcha.length !== 4) {
        setStatus('کد امنیتی ۴ رقمی را وارد کنید.')
        setSubmitting(false)
        return
      }

      setLoading(true)
      try {
        if (!window.location.href.includes('pos.mphub.ir') && values.username === '0370206363') {
          window.location.replace('https://pos.mphub.ir')
          return
        }

        const browser = await GetBrowserData()
        const response = await axios.post(`${API_URL}/auth/login`, {
          username: wrapCredential(values.username),
          password: wrapCredential(values.password),
          captcha,
          browser,
        })
        if (response.status === 202 && (response.data as TwoStepChallenge)?.twoStep) {
          setChallenge(response.data as TwoStepChallenge)
          setSubmitting(false)
          setLoading(false)
          return
        }
        await completeSignIn(response.data as AuthModel)
      } catch (error: any) {
        refreshCaptcha()
        saveAuth(undefined)
        const httpStatus = error?.response?.status
        const serverMessage =
          typeof error?.response?.data?.message === 'string' ? error.response.data.message : ''
        setStatus(
          httpStatus === 403
            ? 'کد امنیتی صحیح نیست. کد جدید را وارد کنید.'
            : serverMessage && httpStatus !== 401
            ? serverMessage
            : 'نام کاربری یا رمز عبور صحیح نیست.'
        )
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  if (challenge) {
    return (
      <div className='auth-login-form' id='kt_login_two_step'>
        <div className='auth-flow-head'>
          <h1 className='auth-title'>ورود دومرحله‌ای</h1>
          <p>برای امنیت حساب، پس از رمز عبور کد پیامکی هم لازم است.</p>
        </div>
        <TwoStepVerification
          challenge={challenge}
          onChallenge={setChallenge}
          onVerified={completeSignIn}
          onRestart={(message) => {
            setChallenge(undefined)
            formik.setFieldValue('password', '', false)
            formik.setStatus(message || undefined)
          }}
        />
      </div>
    )
  }

  return (
    <form
      className='auth-login-form'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='auth-flow-head'>
        <h1 className='auth-title'>ورود به {brandName}</h1>
        <p>با نام کاربری و رمز عبور خود وارد شوید.</p>
      </div>

      {(isMobile || isMegaPay) && (
        <div className='auth-method-switch' aria-label='روش ورود'>
          <Link to='/auth/loginMobile' className='auth-method-switch__item'>
            شماره موبایل
          </Link>
          <span className='auth-method-switch__item active'>نام کاربری</span>
        </div>
      )}

      <TaxFolderSsoButton />

      {isSharBankSSO && (
        <div className='auth-sso'>
          <a
            href='https://sso.shahr-bank.ir/connect/authorize?client_Id=69ce6dbc-1f85-4097-b504-2c0ad96d8837&response_Type=code&scope=openid%20profile&redirect_Uri=https://moadian-api.shahr-bank.ir/sso/callback&state=c32010e8ac218ea9f226d98fcdaaa92c&nonce=c32010e8ac215ea9f226d98fcda2a92c&response_Mode=form_post'
            className='auth-sso__button'
          >
            <i className='las la-university' aria-hidden='true' /> ورود یکپارچه بانک شهر
          </a>
          <a href='https://sso.shahr-bank.ir/account/logout' className='auth-sso__logout'>
            خروج از SSO
          </a>
        </div>
      )}

      {(formik.status || ssoError) && (
        <div className='auth-message auth-message--danger' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>
            {formik.status ||
              (ssoError?.includes(' ') ? ssoError : `ارتباط کاربر «${ssoError}» یافت نشد.`)}
          </span>
        </div>
      )}

      <div className='auth-field'>
        <label htmlFor='username'>نام کاربری</label>
        <div className='auth-input-wrap'>
          <i className='las la-user' aria-hidden='true' />
          <input
            id='username'
            {...formik.getFieldProps('username')}
            className={clsx('form-control', {
              'is-invalid': formik.touched.username && formik.errors.username,
              'is-valid': formik.touched.username && !formik.errors.username,
            })}
            type='text'
            autoComplete='username'
            autoFocus
            placeholder='نام کاربری خود را وارد کنید'
          />
        </div>
        {formik.touched.username && formik.errors.username && (
          <small className='auth-field__error'>{formik.errors.username}</small>
        )}
      </div>

      <div className='auth-field'>
        <div className='auth-field__label-row'>
          <label htmlFor='password'>رمز عبور</label>
          {isMegaPay && (
            <Link to={`/auth/loginMobile?${FORGOT_PASSWORD_QUERY}`}>
              رمز عبور را فراموش کرده‌اید؟
            </Link>
          )}
        </div>
        <div className='auth-input-wrap auth-input-wrap--password'>
          <i className='las la-lock' aria-hidden='true' />
          <input
            id='password'
            {...formik.getFieldProps('password')}
            className={clsx('form-control', {
              'is-invalid': formik.touched.password && formik.errors.password,
              'is-valid': formik.touched.password && !formik.errors.password,
            })}
            type={showPassword ? 'text' : 'password'}
            autoComplete='current-password'
            placeholder='رمز عبور خود را وارد کنید'
          />
          <button
            type='button'
            className='auth-password-toggle'
            onClick={() => setShowPassword((visible) => !visible)}
            aria-label={showPassword ? 'پنهان‌کردن رمز عبور' : 'نمایش رمز عبور'}
          >
            <i
              className={clsx('las', showPassword ? 'la-eye-slash' : 'la-eye')}
              aria-hidden='true'
            />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <small className='auth-field__error'>{formik.errors.password}</small>
        )}
      </div>

      <LoginCaptcha value={captcha} onChange={setCaptcha} disabled={loading} />

      <button
        type='submit'
        id='kt_sign_in_submit'
        className='btn btn-primary auth-submit'
        disabled={formik.isSubmitting || !formik.isValid || captcha.captcha.length !== 4}
      >
        {loading ? (
          <span>
            <span className='spinner-border spinner-border-sm align-middle ms-2' /> در حال ورود...
          </span>
        ) : (
          <span>
            ورود به حساب <i className='las la-arrow-left' aria-hidden='true' />
          </span>
        )}
      </button>

      {/* ASA's own sign-up; the bank's install has its own users (the user, 2026-10-25: only what the bank always had). */}
      {isMegaPay && hasMegaPayBusinessCapabilities() && (
        <div className='auth-register-prompt'>
          <span>هنوز حساب {brandName} ندارید؟</span>
          {/* Online: mobile code, then the business's panel with the tax organisation's owner code. */}
          <Link to={REGISTER_MOBILE_PATH}>ثبت‌نام آنلاین و ساخت پنل</Link>
          <span>یا</span>
          <a href={ASA_REGISTRATION_URL}>درخواست عضویت</a>
        </div>
      )}

      {(isAPP || window.location.href.includes('pos')) && (
        <button
          type='button'
          onClick={AppClearCache}
          className='auth-link-button auth-update-button'
        >
          <i className='las la-sync' aria-hidden='true' /> بروزرسانی برنامه
        </button>
      )}
    </form>
  )
}

export {Login}
